import { Link } from 'react-router-dom'
import { type AuditLogEvent } from '../lib/api'
import {
  eventDetailHint,
  eventDisplayName,
  getActionDisplay,
  isAnonymousEvent,
  slotBadgeClass,
  slotLabel,
} from '../lib/activityActions'

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

interface ActivityTimelineProps {
  events: AuditLogEvent[]
  loading?: boolean
  // When set, the whole row becomes clickable and navigates via the
  // caller's handler — used on the global Activity feed and admin App
  // detail to jump into the actor's user detail page. UserDetail card
  // omits this since the user IS the page.
  onEventClick?: (event: AuditLogEvent) => void
  // When true, the row shows the actor user (name + email). Off in
  // user-scoped views where the user is implicit.
  showUser?: boolean
  // Optional vertical cap with internal scroll — useful when this list
  // is one widget in a multi-card page (e.g. User Activity card).
  // Omit on full-page feeds (Activity.tsx / AppDetail.tsx) so the
  // outer page handles pagination instead.
  maxHeight?: string
}

/**
 * Shared event-row renderer used by every audit feed surface:
 *   /admin/activity         (global, paginated)
 *   /admin/users/<id>       (user-scoped card)
 *   /admin/apps/<name>      (app-scoped, paginated)
 *
 * Earlier each page rolled its own row layout — this consolidated them
 * so action labels, slot badges, drilldown links, detail hints, and IP
 * lines render identically wherever the events show up.
 */
export default function ActivityTimeline({
  events,
  loading,
  onEventClick,
  showUser = false,
  maxHeight,
}: ActivityTimelineProps) {
  if (loading && events.length === 0) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (events.length === 0) {
    return <div className="text-center py-8 text-muted-foreground">No activity recorded yet</div>
  }

  const containerStyle = maxHeight ? { maxHeight, overflow: 'auto' as const } : undefined

  return (
    <div className="space-y-2" style={containerStyle}>
      {events.map((event) => (
        <ActivityEventRow
          key={event.id}
          event={event}
          onClick={onEventClick}
          showUser={showUser}
        />
      ))}
    </div>
  )
}

interface ActivityEventRowProps {
  event: AuditLogEvent
  showUser?: boolean
  onClick?: (event: AuditLogEvent) => void
}

/**
 * Single event row. Exported so callers that need a non-default outer
 * container (e.g. compact card) can reuse the row without the list
 * wrapper.
 */
export function ActivityEventRow({ event, showUser, onClick }: ActivityEventRowProps) {
  const actionDisplay = getActionDisplay(event.action)
  const { Icon } = actionDisplay
  const path =
    event.details && typeof event.details === 'object'
      ? ((event.details as Record<string, unknown>).path as string | undefined)
      : undefined
  const detailHint = eventDetailHint(event)
  const clickable = onClick != null
  const anonymous = isAnonymousEvent(event)

  function handleClick() {
    if (onClick) onClick(event)
  }

  return (
    <div
      className={`p-4 rounded-lg border border-border bg-card transition-colors ${
        clickable ? 'hover:bg-secondary/30 cursor-pointer' : ''
      }`}
      onClick={clickable ? handleClick : undefined}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className={`mt-1 shrink-0 ${actionDisplay.color}`}>
            <Icon className="w-3 h-3" />
          </div>

          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              {showUser && (
                <span
                  className={`font-medium ${
                    anonymous ? 'italic text-muted-foreground' : ''
                  }`}
                >
                  {eventDisplayName(event)}
                </span>
              )}
              <span className={`px-2 py-0.5 rounded text-xs ${actionDisplay.color} bg-current/10`}>
                {actionDisplay.label}
              </span>
              {anonymous && (
                // Public-app visits from no-session callers have user_id
                // NULL and metadata.anonymous=true. Without an explicit
                // badge an admin scanning the feed has no way to tell
                // them apart from "row with no logged-in actor data" —
                // see isAnonymousEvent for the detection rule.
                <span
                  className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-zinc-500/15 text-zinc-600 dark:text-zinc-300"
                  title="No login session — public-access visitor"
                >
                  anon
                </span>
              )}
              {event.slot && (
                <span
                  className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${slotBadgeClass(
                    event.slot,
                  )}`}
                >
                  {slotLabel(event.slot, event.details)}
                </span>
              )}
            </div>

            <div className="text-sm text-muted-foreground flex flex-wrap items-center gap-x-2">
              {showUser && <span>{event.email || '—'}</span>}
              {event.resource_id && (
                <span>
                  {showUser && '→'}
                  {event.resource_type === 'app' ? (
                    // stopPropagation so the row's onClick doesn't also
                    // fire when the link is what the user actually
                    // clicked.
                    <Link
                      to={`/admin/apps/${encodeURIComponent(event.resource_id)}`}
                      onClick={(e) => e.stopPropagation()}
                      className="ml-1 text-xs font-mono text-primary hover:underline"
                    >
                      {event.resource_id}
                    </Link>
                  ) : (
                    <code className="ml-1 text-xs">{event.resource_id}</code>
                  )}
                  {path && path !== '/' && <span className="ml-1 font-mono text-xs">{path}</span>}
                </span>
              )}
            </div>

            {detailHint && (
              <div className="text-xs text-muted-foreground mt-1 italic">{detailHint}</div>
            )}

            {event.ip_address && (
              <div className="text-xs text-muted-foreground mt-1">IP: {event.ip_address}</div>
            )}
          </div>
        </div>

        <div className="text-sm text-muted-foreground text-right shrink-0">
          <div>{formatRelativeTime(event.created_at)}</div>
          <div className="text-xs">{new Date(event.created_at).toLocaleTimeString()}</div>
        </div>
      </div>
    </div>
  )
}
